import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';

const ChatSidebarContext = createContext();

export const SIDEBAR_WIDTH = 340;
export const CHAT_WINDOW_WIDTH = 350;
export const CHAT_WINDOW_HEIGHT = 500;
export const MINIMIZED_CHAT_WIDTH = 220;
export const MINIMIZED_CHAT_HEIGHT = 48;
export const MAX_OPEN_CHATS = 8;
export const CHAT_GAP = 10;

const STORAGE_KEY = 'openChats';

// Загрузка из localStorage
const loadFromStorage = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                openChatIds: parsed.openChatIds || [],
                minimizedChatIds: parsed.minimizedChatIds || [],
                isSidebarOpen: parsed.isSidebarOpen || false,
            };
        }
    } catch (e) {
        console.error('Error loading chats from storage:', e);
    }
    return { openChatIds: [], minimizedChatIds: [], isSidebarOpen: false };
};

// Сохранение в localStorage
const saveToStorage = (openChatIds, minimizedChatIds, isSidebarOpen) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ openChatIds, minimizedChatIds, isSidebarOpen }));
    } catch (e) {
        console.error('Error saving chats to storage:', e);
    }
};

export function ChatSidebarProvider({ children }) {
    const [isOpen, setIsOpen] = useState(() => loadFromStorage().isSidebarOpen);
    const [openChatIds, setOpenChatIds] = useState(() => loadFromStorage().openChatIds);
    const [minimizedChatIds, setMinimizedChatIds] = useState(() => loadFromStorage().minimizedChatIds);
    // Данные о сделках для каждого чата (employeeId -> { item, type })
    const [chatDealData, setChatDealData] = useState({});

    // Сохраняем в localStorage при изменении
    useEffect(() => {
        saveToStorage(openChatIds, minimizedChatIds, isOpen);
    }, [openChatIds, minimizedChatIds, isOpen]);

    const toggleSidebar = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const openSidebar = useCallback(() => {
        setIsOpen(true);
    }, []);

    const closeSidebar = useCallback(() => {
        setIsOpen(false);
    }, []);

    // Открыть чат (развёрнутым) с опциональными данными о сделке
    const openChat = useCallback((employeeId, dealData = null) => {
        // Сохраняем данные о сделке если они переданы
        if (dealData) {
            setChatDealData(prev => ({
                ...prev,
                [employeeId]: dealData
            }));
        }

        setOpenChatIds(prev => {
            if (prev.includes(employeeId)) {
                // Уже открыт - разворачиваем если свёрнут
                setMinimizedChatIds(m => m.filter(id => id !== employeeId));
                return prev;
            }
            // Ограничение на максимум чатов
            if (prev.length >= MAX_OPEN_CHATS) {
                toast.warning(`Максимум ${MAX_OPEN_CHATS} открытых чатов. Закройте один из чатов.`, {
                    position: 'top-right',
                    autoClose: 4000,
                });
                return prev;
            }
            return [...prev, employeeId];
        });
    }, []);

    const closeChat = useCallback((employeeId) => {
        setOpenChatIds(prev => prev.filter(id => id !== employeeId));
        setMinimizedChatIds(prev => prev.filter(id => id !== employeeId));
        // Удаляем данные о сделке при закрытии чата
        setChatDealData(prev => {
            const newData = { ...prev };
            delete newData[employeeId];
            return newData;
        });
    }, []);

    // Переключение свёрнут/развёрнут
    const toggleMinimizeChat = useCallback((employeeId) => {
        setMinimizedChatIds(prev => {
            if (prev.includes(employeeId)) {
                // Развернуть
                return prev.filter(id => id !== employeeId);
            } else {
                // Свернуть
                return [...prev, employeeId];
            }
        });
    }, []);

    // Проверка свёрнут ли чат
    const isChatMinimized = useCallback((employeeId) => {
        return minimizedChatIds.includes(employeeId);
    }, [minimizedChatIds]);

    // Получение данных о сделке для чата
    const getChatDealData = useCallback((employeeId) => {
        return chatDealData[employeeId] || null;
    }, [chatDealData]);

    // Очистка данных о сделке после отправки первого сообщения
    const clearChatDealData = useCallback((employeeId) => {
        setChatDealData(prev => {
            const newData = { ...prev };
            delete newData[employeeId];
            return newData;
        });
    }, []);

    // Данные для попап-чата (открывается как модалка, без панели чатов)
    const [popupChatData, setPopupChatData] = useState(null);

    // Открыть чат как попап (модалка поверх контента)
    const openPopupChat = useCallback((chatData) => {
        setPopupChatData(chatData);
    }, []);

    // Закрыть попап-чат
    const closePopupChat = useCallback(() => {
        setPopupChatData(null);
    }, []);

    const value = {
        isOpen,
        openChatIds,
        minimizedChatIds,
        chatDealData,
        popupChatData,
        toggleSidebar,
        openSidebar,
        closeSidebar,
        openChat,
        closeChat,
        toggleMinimizeChat,
        isChatMinimized,
        getChatDealData,
        clearChatDealData,
        openPopupChat,
        closePopupChat,
    };

    return (
        <ChatSidebarContext.Provider value={value}>
            {children}
        </ChatSidebarContext.Provider>
    );
}

export function useChatSidebar() {
    const context = useContext(ChatSidebarContext);
    if (!context) {
        throw new Error('useChatSidebar must be used within ChatSidebarProvider');
    }
    return context;
}

export default ChatSidebarContext;
